import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { stagger, blurUp, lineUp, ruleGrow } from "../utils/motion";

import slide1 from "../assets/bbs/NewPhotos/optimized/DSC07842.jpg";
import slide2 from "../assets/bbs/NewPhotos/optimized/DSC07860.jpg";
import slide3 from "../assets/bbs/NewPhotos/optimized/DSC07875.jpg";

const slides = [slide1, slide2, slide3];
const ROTATION_MS = 7000;

const promises = ["Postavíme", "Obsloužíme", "Uklidíme"];

const Hero = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <Wrapper>
      <div className="slides">
        <AnimatePresence>
          <motion.div
            key={index}
            className="slide"
            style={{ backgroundImage: `url(${slides[index]})` }}
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 1, scale: 1.08 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.2, ease: "easeInOut" },
              scale: { duration: (ROTATION_MS + 1500) / 1000, ease: "linear" },
            }}
          />
        </AnimatePresence>
        <div className="overlay" />
      </div>

      <div className="content-wrap">
        <motion.div
          className="content"
          variants={stagger(0.11, 0.55)}
          initial="hidden"
          animate="show"
        >
          <motion.span className="eyebrow" variants={blurUp}>
            <motion.span className="rule" aria-hidden="true" variants={ruleGrow} />
            <span className="script">Catering &amp; půjčovna v Pardubicích</span>
          </motion.span>
          <h1>
            <span className="line">
              <motion.span className="line-inner" variants={lineUp}>
                Firemní a rodinné
              </motion.span>
            </span>
            <span className="line">
              <motion.span className="line-inner" variants={lineUp}>
                akce <em>na klíč</em>
              </motion.span>
            </span>
          </h1>
          <motion.p variants={blurUp}>
            Pronájem party stanů, kompletní catering a proškolený barový tým.
            Sestavíme menu podle vašeho přání — pro svatbu, oslavu i firemní
            večírek.
          </motion.p>
          <motion.div className="cta-row" variants={blurUp}>
            <Link to="/pronajem-party-stanu-vybaveni" className="btn primary-cta">
              Prohlédnout vybavení
            </Link>
            <Link to="/barovy-catering-na-akce" className="btn ghost-cta">
              Catering
            </Link>
          </motion.div>

          <motion.ul className="promises" aria-label="Jak pracujeme" variants={blurUp}>
            {promises.map((item, i) => (
              <li key={item}>
                <span className="p-num">0{i + 1}</span>
                {item}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          className="bottom-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.8, delay: 1.4 } }}
        >
          <div className="dots">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === index ? "dot active" : "dot"}
                aria-label={`Snímek ${i + 1}`}
                onClick={() => setIndex(i)}
              />
            ))}
            <span className="counter">
              0{index + 1} <span className="of">/ 0{slides.length}</span>
            </span>
          </div>

          <div className="scroll-hint" aria-hidden="true">
            <span className="label">scroll</span>
            <span className="scroll-line" />
          </div>
        </motion.div>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: 80vh;
  margin-top: -5rem;
  overflow: hidden;
  color: var(--clr-cream);

  .slides {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: var(--clr-dark-1);
  }
  .slide {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    will-change: transform, opacity;
  }
  .overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        110deg,
        rgba(20, 13, 9, 0.92) 0%,
        rgba(20, 13, 9, 0.6) 55%,
        rgba(20, 13, 9, 0.28) 100%
      ),
      linear-gradient(to top, rgba(20, 13, 9, 0.55) 0%, transparent 30%);
  }

  .content-wrap {
    position: relative;
    z-index: 2;
    width: 90vw;
    max-width: var(--max-width);
    margin: 0 auto;
    min-height: 80vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 7rem 0 4.5rem;
  }

  .content {
    max-width: 680px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 1rem;
  }
  .eyebrow .rule {
    display: inline-block;
    width: 48px;
    height: 1px;
    background: var(--clr-accent);
    transform-origin: left;
  }
  .eyebrow .script {
    font-size: 1.45rem;
    color: var(--clr-accent-light);
  }

  h1 {
    color: var(--clr-cream);
    font-size: clamp(2.6rem, 7vw, 4.75rem);
    margin-bottom: 1.5rem;
    line-height: 1.04;
    letter-spacing: -0.02em;
  }
  h1 .line {
    display: block;
    overflow: hidden;
    padding-bottom: 0.08em;
    margin-bottom: -0.08em;
  }
  h1 .line-inner {
    display: block;
    will-change: transform;
  }
  h1 em {
    font-style: italic;
    font-weight: 500;
    color: var(--clr-accent-light);
  }

  p {
    line-height: 1.75;
    color: rgba(250, 244, 234, 0.88);
    margin-bottom: 2.25rem;
    font-size: 1.05rem;
    max-width: 52ch;
  }

  .cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
  }

  .primary-cta {
    padding: 0.95rem 1.9rem;
    font-size: 0.95rem;
  }

  .ghost-cta {
    background: transparent;
    border: 1px solid rgba(250, 244, 234, 0.55);
    color: var(--clr-cream);
    padding: 0.85rem 1.7rem;
    &:hover {
      background: var(--clr-cream);
      border-color: var(--clr-cream);
      color: var(--clr-charcoal-1);
    }
  }

  .promises {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-top: 3rem;
    padding: 1.4rem 0 0;
    border-top: 1px solid rgba(250, 244, 234, 0.18);
    max-width: 560px;
  }
  .promises li {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    font-family: var(--ff-display);
    font-size: 1.15rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: rgba(250, 244, 234, 0.95);
    padding-right: 2.25rem;
  }
  .p-num {
    font-family: var(--ff-body);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: var(--clr-accent);
  }

  .bottom-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: 3rem;
  }

  .dots {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .dot {
    width: 28px;
    height: 3px;
    border-radius: 2px;
    background: rgba(250, 244, 234, 0.3);
    border: none;
    cursor: pointer;
    transition: var(--transition);
    padding: 0;
  }
  .dot.active {
    background: var(--clr-accent);
    width: 48px;
  }
  .dot:hover {
    background: rgba(250, 244, 234, 0.7);
  }
  .counter {
    margin-left: 1rem;
    font-family: var(--ff-display);
    font-size: 1rem;
    color: var(--clr-cream);
    letter-spacing: 0.06em;
  }
  .counter .of {
    color: rgba(250, 244, 234, 0.5);
    font-size: 0.85rem;
  }

  .scroll-hint {
    display: none;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
  }
  .scroll-hint .label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.3em;
    color: rgba(250, 244, 234, 0.6);
    writing-mode: vertical-rl;
  }
  .scroll-hint .scroll-line {
    width: 1px;
    height: 56px;
    background: rgba(250, 244, 234, 0.25);
    position: relative;
    overflow: hidden;
  }
  .scroll-hint .scroll-line::after {
    content: "";
    position: absolute;
    left: 0;
    top: -50%;
    width: 100%;
    height: 50%;
    background: var(--clr-accent);
    animation: scrollPulse 2.2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
  }
  @keyframes scrollPulse {
    0% {
      top: -50%;
    }
    100% {
      top: 110%;
    }
  }

  @media (min-width: 776px) {
    min-height: 88vh;
    .content-wrap {
      min-height: 88vh;
      padding: 8rem 0 4.5rem;
    }
    p {
      font-size: 1.1rem;
    }
    .scroll-hint {
      display: flex;
    }
  }

  @media (min-width: 992px) {
    min-height: min(96vh, 820px);
    .content-wrap {
      min-height: min(96vh, 820px);
      padding: 9rem 0 5rem;
    }
    p {
      font-size: 1.15rem;
    }
    .promises li {
      font-size: 1.25rem;
    }
  }
`;

export default Hero;
